import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Star, Zap, Clock, ChevronDown, Instagram, Tag, ArrowRight, Video, Globe, Bell, Ticket, ExternalLink } from 'lucide-react';
import { directoryApi } from '../../lib/api';
import { DfwGymCard, VideoEmbed, UpcomingClasses, scrollToRaceAlerts } from './dfw/DfwGymCard';
import { NearbyRecovery } from './dfw/NearbyRecovery';
import { VideoSubmitModal, ClaimListingModal } from './dfw/DfwGymModals';
import { RaceAlertCapture } from './dfw/RaceAlertCapture';

const CITY_CONFIGS = {
  dfw: {
    directoryId: 'dfw-hyrox',
    testPrefix: 'dfw',
    kicker: 'HYROX · Dallas–Fort Worth',
    h1: 'The Best HYROX Training Gyms in Dallas–Fort Worth',
    jsonLdName: 'The Best HYROX Training Gyms in Dallas-Fort Worth',
    intro: (
      <>
        If you're training for a <strong className="text-zinc-200">HYROX race in Dallas–Fort Worth</strong>, finding the right gym is everything.
        You need SkiErgs, rowers, sled tracks, and wall balls — not just treadmills and dumbbells. This directory covers the official
        HYROX Training Center, affiliate gyms, and specialty studios across the metroplex — with videos, pricing, and direct class-purchase links.
      </>
    ),
    stats: (count) => [[`${count || 31}`, 'Gyms listed'], ['1', 'Official HYROX Training Center'], ['$15+', 'Drop-ins from'], ['13', 'Cities covered']],
    featuredNav: [['#f45-dallas-arena', 'Featured: F45 Dallas Arena'], ['#hidden-gym-allen', 'Featured: Hidden Gym']],
    crossPromo: {
      eyebrow: 'Build your running engine',
      title: 'The Best Running Clubs Across the USA — 4 Dallas Clubs Scored',
      text: "HYROX races are won on the 8 runs. We independently scored 34 US run clubs on pace honesty, no-drop policy and first-timer experience — Dallas's Run It Up Dallas rated 84/100 (free, beginner-friendly, strong no-drop policy) alongside three more Dallas crews.",
      href: '/blogs/article/the-best-running-clubs-across-the-usa-where-community-meets-the-pavement#runitupdallas',
      cta: 'Read the run club guide',
    },
    alertBanner: 'Get Dallas race-date alerts + invites to local HYROX simulation events:',
    areaSections: [
      { key: 'Official HYROX Performance Centers', id: 'official-centers', blurb: 'Officially sanctioned HYROX facilities with competition equipment and certified programming.' },
      { key: 'Dallas Proper', id: 'dallas-proper', blurb: 'Race-ready training inside the Dallas city limits — from downtown boutiques to Design District race-prep hubs.' },
      { key: 'Surrounding DFW', id: 'surrounding-dfw', blurb: 'Fort Worth, Richardson, Plano and McKinney — HYROX programming across the metroplex.' },
    ],
    faqs: [
      {
        q: 'How much are HYROX classes at F45 Dallas Arena?',
        a: 'F45 Dallas Arena (1775 Payne Street STE 150, Victory Park) charges $20 per 90-minute specialty HYROX class — Fridays at 6:00 PM and Saturdays at 11:30 AM, not included in a standard F45 membership. The gym is an official F45 x HYROX partner running a stage-based 12-week program (Aug 28 – Nov 13) ahead of the Dallas race, with full race simulations on September 13 and October 25 and a free run club on Tuesday mornings. Class spots are limited and bookable through their online calendar.',
      },
      {
        q: 'Which gym near Allen offers dedicated HYROX training?',
        a: 'Hidden Gym — Allen (109 N Greenville) runs a dedicated HYROX program for athletes across Plano, McKinney, Richardson and North Dallas, from beginners through experienced racers. Athletes train on BLK BOX competition sleds, wall balls and targets, kettlebells, sandbags, a 15-meter sled rope, and Concept2 SkiErgs and RowErgs. Classes run Mon–Thu 6 AM & 7 PM, Fri 6 AM and Sat 9 AM & 12 PM, a free 3-day pass is available, and programming is delivered through the Hidden Gym training app.',
      },
      {
        q: 'Which Dallas gym offers 24/7 access for HYROX training with race simulation?',
        a: "Hunger in the Wild is an official HYROX Training Center in Dallas's Design District with 24/7 member access. Weekly programming splits HYROX into Engine (Mon/Thu), Strength (Tue) and Performance (Wed/Sat) sessions on CENTR sleds and ropes, Concept2 ergs and a dedicated turf lane. Standard membership is $220 per 4 weeks, drop-ins are $30 — and code HYROX25 takes 25% off your first payment through the Dallas race in November 2026.",
      },
      {
        q: 'How much does PITFIT in Dallas charge for HYROX training classes?',
        a: 'PITFIT charges $15 for a drop-in class, offers a 3-class pack for $50, and unlimited monthly membership for $279. Use code "Ninja" at checkout for a special offer. The Knox/Henderson facility features dedicated HYROX Total Body classes led by Sara Yingling and Evan Conrad.',
      },
      {
        q: 'Which Dallas CrossFit gyms run dedicated HYROX classes?',
        a: 'CrossFit Love Field (9233 Denton Dr #100) runs dedicated HYROX classes Tuesdays and Thursdays at 7 PM and Sundays at 9:30 AM in a 9,000+ sq ft facility. Drop-ins are $25 (credited toward joining), and new members get 2 months of unlimited training for $99.',
      },
      {
        q: 'Which Dallas gym runs early-morning coached HYROX classes?',
        a: 'Dallas Performance Systems (2651 Fondren Drive, Suite B) runs coach-led HYROX classes on Wednesdays and Fridays at 6:00 AM, currently coached by Dez. The confirmed drop-in rate is $40, and the facility supports hybrid training with space for running, sled work, strength and conditioning.',
      },
    ],
  },
  houston: {
    directoryId: 'houston-hyrox',
    testPrefix: 'houston',
    kicker: 'HYROX · Houston',
    h1: 'The Best HYROX Training Gyms in Houston',
    jsonLdName: 'The Best HYROX Training Gyms in Houston',
    intro: (
      <>
        If you're training for <strong className="text-zinc-200">HYROX in Houston</strong>, you need more than treadmills — you need SkiErgs, sleds,
        wall balls and coaches who know the race standard. This directory covers Houston's HTC-X certified facility, every official HYROX
        Training Club we could verify, and the best HYROX-ready gyms across the metro — with pricing where published.
      </>
    ),
    stats: (count) => [[`${count || 12}`, 'Gyms listed'], ['2', 'Official training centers'], ['$25', 'First class from'], ['26+', 'HYROX-affiliated gyms']],
    areaSections: [
      { key: 'Official HYROX Training Centers', id: 'official-training-centers', blurb: "Houston's HTC-X certified facility and licensed HYROX training centers — purpose-built for the race standard." },
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified official HYROX Training Clubs across the metro, from River Oaks to the east side.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-ready', blurb: 'F45 affiliates, capped prep courses and flexible session packs for targeted station work.' },
    ],
    faqs: [
      { q: 'Is there a dedicated HYROX training facility in Houston?', a: "Yes — HYROX Houston is the city's only HTC-X certified dedicated HYROX facility, with full race stations and a $200/month Founders Rate for the first 50 members." },
      { q: 'Which Houston gyms are official HYROX Training Clubs?', a: 'Verified official Training Clubs include Adrenaline Fitness and Performance, Breach Fitness, BFT River Oaks, HNS Training Facility, Illuminate Sweat, O Athletik and For Our City Training Club — with 26+ HYROX-affiliated gyms across the greater Houston region.' },
      { q: 'How much does HYROX training cost in Houston?', a: 'First classes start around $25 (Breach Fitness), single HYROX sessions run about $40 (MRT Optimal Fitness), and dedicated memberships range from $120/month (Urban Athletics) to $200/month (HYROX Houston Founders Rate).' },
      { q: 'Does Houston host an official HYROX race?', a: 'Yes — Houston is a regular stop on the HYROX North America calendar. Most local gyms build 12–16 week prep blocks ahead of the race; check hyrox.com for current dates.' },
    ],
  },
  austin: {
    directoryId: 'austin-hyrox',
    testPrefix: 'austin',
    kicker: 'HYROX · Austin',
    h1: 'The Best HYROX Training Gyms in Austin',
    jsonLdName: 'The Best HYROX Training Gyms in Austin',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Austin</strong>? The ATX scene is loaded: eight official HYROX Training Clubs
        plus a bench of serious hybrid gyms running race simulations, station work and engine development. This directory covers every
        verified option from Zilker to Round Rock — with pricing where published.
      </>
    ),
    stats: (count) => [[`${count || 12}`, 'Gyms listed'], ['8', 'Official training clubs'], ['$19+', 'Day passes from'], ['$35', 'HYROX SIM events']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'All eight verified official HYROX Training Clubs — from Burnet Road to Round Rock.' },
      { key: 'HYROX-Ready Training Gyms', id: 'hyrox-ready-gyms', blurb: 'Dedicated HYROX programs and hybrid strength-and-conditioning gyms with race-focused coaching.' },
    ],
    faqs: [
      { q: 'Which Austin gyms are official HYROX Training Clubs?', a: "CrossFit Central (Burnet Road), East Austin Athletic Club, SWEAT440 Zilker, Dane's Body Shop, Body Fit Training Austin, D1 Training Jollyville, Gold's Gym South Central and Los Campeones Round Rock are all verified official HYROX Training Clubs." },
      { q: 'Where can I do a HYROX race simulation in Austin?', a: 'CrossFit Central on Burnet Road runs full HYROX SIM events ($35 for non-members), and East Austin Athletic Club hosts in-house simulation races throughout the year.' },
      { q: 'How much does HYROX training cost in Austin?', a: 'Memberships start around $89–99/month (Los Campeones, SWEAT440, Sola CrossFit), with race simulations around $35 and day passes from $19 in Round Rock.' },
      { q: 'Does Austin host an official HYROX race?', a: "Austin doesn't currently have its own HYROX event — most Austin athletes race HYROX Dallas or HYROX Houston, both an easy drive away. Local Training Clubs schedule their prep blocks around the Texas race calendar." },
    ],
  },
  denver: {
    directoryId: 'denver-hyrox',
    testPrefix: 'denver',
    kicker: 'HYROX · Denver',
    h1: 'The Best HYROX Training Gyms in Denver',
    jsonLdName: 'The Best HYROX Training Gyms in Denver',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Denver</strong>? The Mile High scene is deep — an official Training Club with
        two metro locations, certified affiliates from Centennial to South Broadway, and F45 studios running HYROX Fridays. This directory
        covers every verified option, with class times, equipment and membership links.
      </>
    ),
    stats: (count) => [[`${count || 7}`, 'Gyms listed'], ['5', 'Official clubs & affiliates'], ['24/7', 'Fob access at Arsenal'], ['2', 'Arsenal metro locations']],
    featuredNav: ['#arsenal-health-denver', 'Featured: Arsenal Health'],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Certified HYROX clubs and affiliates from Centennial to downtown Denver.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-ready', blurb: 'F45 x HYROX affiliate studios with race sims and drop-in friendly sessions.' },
    ],
    faqs: [
      { q: 'Which Denver gym is best for HYROX training?', a: "Arsenal Health + Fitness is the standout — an official HYROX Training Club with official Centr sleds/ropes, Concept2 ergs, HYROX classes at both Arvada and DTC, plus a medical and recovery stack most gyms can't match." },
      { q: "What are Arsenal Health's HYROX class times?", a: 'Arvada: Mon/Wed/Fri at 1 PM, Tue/Thu at 7:30 AM, Wed at 5:30 PM and Sat 9–10:30 AM. DTC: Wednesdays at 9 AM and Noon.' },
      { q: 'Which Denver gyms are official HYROX affiliates?', a: 'Arsenal Health + Fitness (Arvada/DTC), Elite Performance Center (Centennial), Traverse Fitness (SoBo), Colfax Strong (downtown) and DTC CrossFit are all verified official HYROX clubs or affiliates.' },
      { q: 'Does Denver host an official HYROX race?', a: 'Yes — Denver appears on the HYROX North America calendar. Local clubs like Traverse Fitness also organize group travel to other US races; check hyrox.com for current dates.' },
    ],
  },
  tampa: {
    directoryId: 'tampa-hyrox',
    testPrefix: 'tampa',
    kicker: 'HYROX · Tampa',
    h1: 'The Best HYROX Training Gyms in Tampa',
    jsonLdName: 'The Best HYROX Training Gyms in Tampa',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Tampa</strong>? The Bay is stacked — official Training Clubs from Gas Worx
        to Riverview, an official Training Center, and F45 Largo East: the only F45 in Tampa Bay running real Centr HYROX equipment,
        with a full HYROX simulation on September 12.
      </>
    ),
    stats: (count) => [[`${count || 15}`, 'Gyms listed'], ['3', 'Official HYROX facilities'], ['Sep 12', 'Full HYROX sim at F45 Largo'], ['✓', 'Doubles & relay prep']],
    featuredNav: ['#f45-largo-east', 'Featured: F45 Largo East'],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified official HYROX clubs and the Riverview Training Center.' },
      { key: 'HYROX Programs & Classes', id: 'hyrox-programs-classes', blurb: 'Studios running dedicated, coach-led HYROX training classes around the Bay.' },
      { key: 'HYROX-Ready Training Gyms', id: 'hyrox-ready-gyms', blurb: 'Dedicated HYROX classes, F45 affiliates and functional boxes across the Bay.' },
    ],
    faqs: [
      { q: 'Which Tampa gyms are official HYROX Training Clubs?', a: "Gold's Gym Tampa Gas Worx, AutoFit Fam (official Training Center, Riverview) and FitCity CrossFit are verified official HYROX facilities in the Tampa area." },
      { q: 'Where can I do a full HYROX simulation in Tampa Bay?', a: 'F45 Training Largo East runs a full HYROX simulation on September 12, 2026 (booked via Eventbrite) — they have already hosted two HALFROX events with 100+ athletes on official Centr sleds, wall-ball targets and Concept2 ergs.' },
      { q: 'Where can I train for HYROX doubles or relay in Tampa?', a: "Gold's Gym Gas Worx runs structured classes specifically for race prep, doubles and relay teams at 802 N 12th St near downtown." },
      { q: 'Does Tampa host an official HYROX race?', a: 'Yes — Tampa is on the HYROX North America calendar. Most local clubs build 12–16 week prep blocks ahead of the event; check hyrox.com for dates.' },
    ],
  },
  slc: {
    directoryId: 'slc-hyrox',
    testPrefix: 'slc',
    kicker: 'HYROX · Salt Lake City',
    h1: 'The Best HYROX Training Gyms in Salt Lake City',
    jsonLdName: 'The Best HYROX Training Gyms in Salt Lake City',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Salt Lake City</strong>? Utah pairs an accredited official facility with
        the Wasatch's best functional boxes — verified options from Clearfield to South Jordan.
      </>
    ),
    stats: (count) => [[`${count || 6}`, 'Gyms listed'], ['2', 'Official HYROX facilities'], ['$35', '5-class intro at F45'], ['4,300ft', 'Altitude advantage']],
    featuredNav: ['#f45-training-sugarhouse', 'Featured: F45 Sugarhouse'],
    areaSections: [
      { key: 'Official HYROX Facilities', id: 'official-facilities', blurb: 'The accredited Athletic Republic facility and the official Training Club in SLC.' },
      { key: 'HYROX-Ready Training Gyms', id: 'hyrox-ready-gyms', blurb: 'CrossFit boxes maintaining full SkiErg / rower / sled / wall-ball setups.' },
    ],
    faqs: [
      { q: 'Does F45 Sugarhouse have dedicated HYROX classes?', a: 'Yes — F45 Training Sugarhouse runs dedicated HYROX classes on Mondays at 6:45 PM and Fridays at 5:30 PM, on official CENTR HYROX sleds, sled-pull ropes, sandbags and wall balls. New guests can use the 5-classes-for-$35 intro on HYROX classes, and every F45 membership covers HYROX classes and SIMs.' },
      { q: 'Is there an official HYROX training facility in Salt Lake City?', a: 'Yes — Athletic Republic North Davis in Clearfield is an accredited official HYROX facility, and The Front Climbing Club in SLC is an official HYROX Training Club.' },
      { q: 'Which SLC boxes have full HYROX stations?', a: 'CrossFit SLC, CrossFit Sugarhouse, CrossFit Millcreek and CrossFit South Jordan all maintain SkiErgs, rowers, sleds and wall-ball stations for race simulation.' },
      { q: 'Does Salt Lake City host an official HYROX race?', a: 'Yes — HYROX Salt Lake City runs September 18–20, 2026; check hyrox.com for venue details and tickets.' },
    ],
  },
  vegas: {
    directoryId: 'vegas-hyrox',
    testPrefix: 'vegas',
    kicker: 'HYROX · Las Vegas',
    h1: 'The Best HYROX Training Gyms in Las Vegas',
    jsonLdName: 'The Best HYROX Training Gyms in Las Vegas',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Las Vegas</strong>? Sin City has one of the deepest rosters in the country —
        nine official Training Clubs plus race-sim programs from the Strip to Henderson.
      </>
    ),
    stats: (count) => [[`${count || 11}`, 'Gyms listed'], ['9', 'Official Training Clubs'], ['1', 'Run-specialist club'], ['✓', 'Race-sim classes']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Nine verified official Training Clubs from the Strip to Henderson.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-ready', blurb: 'Race-simulation classes and HYROX-aligned strength & conditioning.' },
    ],
    faqs: [
      { q: 'Which Las Vegas gyms are official HYROX Training Clubs?', a: 'Camp Rhino, Tony Cress Training Center, ENERGYM, IVI Performance, Project Fitness, CrossFit Social City, The Hybrid Engine, The Run Room and The Strip Barbell are all verified official HYROX Training Clubs.' },
      { q: 'Where can I do HYROX race simulations in Las Vegas?', a: 'CrossFit Las Vegas runs dedicated HYROX classes with race-simulation drills, and The Hybrid Engine programs specifically for hybrid racing year-round.' },
      { q: 'Does Las Vegas host an official HYROX race?', a: 'Yes — Las Vegas is a marquee stop on the HYROX North America calendar; check hyrox.com for current dates.' },
    ],
  },
  portland: {
    directoryId: 'portland-hyrox',
    testPrefix: 'portland',
    kicker: 'HYROX · Portland',
    h1: 'The Best HYROX Training Gyms in Portland',
    jsonLdName: 'The Best HYROX Training Gyms in Portland',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Portland</strong>? The metro runs from the Pearl District to Tigard —
        official Training Clubs, an official F45 HYROX affiliate, a DPT-led performance center and structured race-format programs.
      </>
    ),
    stats: (count) => [[`${count || 6}`, 'Gyms listed'], ['3', 'Official clubs & affiliates'], ['$20', 'HYROX drop-ins from'], ['DPT', 'Clinician-led sims']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'HYROX-affiliated clubs in the Pearl District, Beaverton and Hillsboro.' },
      { key: 'HYROX-Ready Training Gyms', id: 'hyrox-ready-gyms', blurb: 'Simulation sessions, Pearl District programming and goal-mapped training.' },
    ],
    faqs: [
      { q: 'Which Portland gyms are official HYROX Training Clubs?', a: "F45 Training Pearl District (official HYROX affiliate with in-studio signage), Beaverton Strength & Conditioning and NXT Level Fitness (Hillsboro) are the metro's official HYROX-affiliated clubs." },
      { q: 'How much does HYROX training cost in Portland?', a: 'F45 Pearl District runs $20 HYROX class drop-ins, $35 quarterly timed HYROX Sims and a $25 seven-day trial for local residents — with HYROX-specific classes every Sunday.' },
      { q: 'Where can I do a HYROX simulation in Portland?', a: 'F45 Pearl District hosts quarterly timed HYROX simulation events, and APEX PWR in Tigard runs HYROX simulation sessions on a 40-yard turf strip led by Dr. Kevin Le, DPT.' },
      { q: 'Does Portland host an official HYROX race?', a: 'Check hyrox.com for the current North America calendar — Pacific Northwest athletes typically race Seattle or fly to the nearest event, and local clubs schedule prep blocks around it.' },
    ],
  },
  anaheim: {
    directoryId: 'anaheim-hyrox',
    testPrefix: 'anaheim',
    kicker: 'HYROX · Anaheim',
    h1: 'The Best HYROX Training Gyms in Anaheim',
    jsonLdName: 'The Best HYROX Training Gyms in Anaheim',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Anaheim</strong>? Orange County brings serious firepower — official
        HYROX Training Clubs in Irvine and HYROX-ready floors across the metro, from Anaheim to Costa Mesa.
      </>
    ),
    stats: (count) => [[`${count || 5}`, 'Gyms listed'], ['2', 'Official Training Clubs'], ['OC', 'Anaheim to Costa Mesa'], ['13', 'Cities covered']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified official HYROX Training Clubs serving Orange County athletes.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'HYROX-format programs and hybrid floors across Orange County.' },
    ],
    faqs: [
      { q: 'Does Anaheim host an official HYROX race?', a: 'Yes — Anaheim is a regular stop on the HYROX North America calendar at the Anaheim Convention Center; check hyrox.com for dates.' },
    ],
  },
  sandiego: {
    directoryId: 'sandiego-hyrox',
    testPrefix: 'sandiego',
    kicker: 'HYROX · San Diego',
    h1: 'The Best HYROX Training Gyms in San Diego',
    jsonLdName: 'The Best HYROX Training Gyms in San Diego',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in San Diego</strong>? America's Finest City has one of the deepest rosters on
        the West Coast — an official Training Club in Oceanside, affiliates from Scripps Ranch to the Gaslamp Quarter, and BFT partners
        along the coast.
      </>
    ),
    stats: (count) => [[`${count || 13}`, 'Gyms listed'], ['10', 'Official clubs & affiliates'], ['$30', '3-class intro at MROC'], ['2014', 'MROC training since']],
    featuredNav: ['#mroc-training-oceanside', 'Featured: MROC Training'],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified affiliates and training clubs from Scripps Ranch to downtown and Pacific Beach.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-ready', blurb: 'HYROX-style programs and BFT partner formats across the coast.' },
    ],
    faqs: [
      { q: 'Is there an official HYROX Training Club in San Diego?', a: "Yes — MROC Training in Oceanside is North County's official HYROX Training Club, with every trainer certified as a HYROX Performance Coach and approved CENTR + Concept2 equipment. Affiliates like Fitness Quest 10, Mighty Fit SD and Invictus cover the city proper." },
      { q: 'How much does HYROX training cost in San Diego?', a: 'MROC memberships run $169–209/month with a 3-classes-for-$30 intro offer. Affiliates around the city typically range $150–300/month.' },
      { q: 'Where can I do a HYROX race simulation in San Diego?', a: 'MROC Training runs simulations on official race-day equipment, and CrossFit ATR and Invictus both run race-prep programming with full station work.' },
    ],
  },
  nashville: {
    directoryId: 'nashville-hyrox',
    testPrefix: 'nashville',
    kicker: 'HYROX · Nashville',
    h1: 'The Best HYROX Training Gyms in Nashville',
    jsonLdName: 'The Best HYROX Training Gyms in Nashville',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Nashville</strong>? Music City's scene stretches from 12South to
        Murfreesboro — official affiliates, a PRVN-programmed training club and F45 HYROX studios.
      </>
    ),
    stats: (count) => [[`${count || 8}`, 'Gyms listed'], ['5', 'Official clubs & affiliates'], ['$119', 'Memberships from'], ['Free', 'Pass at Blueprint']],
    featuredNav: ['#blueprint-fitness-murfreesboro', 'Featured: Blueprint Fitness'],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'PRVN HQ, dedicated HYROX programs and official affiliates across the metro.' },
      { key: 'HYROX-Ready Training Gyms', id: 'hyrox-ready-gyms', blurb: 'F45 x HYROX studios and race-ready boxes.' },
    ],
    faqs: [
      { q: 'Which Nashville gyms are official HYROX affiliates?', a: 'Blueprint Fitness (Murfreesboro), PRVN HQ (Berry Hill) and CrossFit Rising Sun are official affiliates, with CrossFit Music City running a dedicated HYROX program in 12South.' },
      { q: "What are Blueprint Fitness's HYROX class times?", a: 'Monday, Wednesday and Friday at 5:00 AM, 8:00 AM, noon, 4:30 PM and 5:30 PM. The gym is open 4:30 AM to 9:00 PM daily, with unlimited memberships at $119–159/month.' },
      { q: 'Does Nashville host an official HYROX race?', a: 'Yes — Nashville appears on the HYROX North America calendar; check hyrox.com for current dates.' },
    ],
  },
  boston: {
    directoryId: 'boston-hyrox',
    testPrefix: 'boston',
    kicker: 'HYROX · Boston',
    h1: 'The Best HYROX Training Gyms in Boston',
    jsonLdName: 'The Best HYROX Training Gyms in Boston',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Boston</strong>? The Hub is loaded — official partners from Back Bay to the
        Seaport, a clinician-led training club running the HYROX Boston Run Club, and serious CrossFit boxes with race-ready floors.
      </>
    ),
    stats: (count) => [[`${count || 11}`, 'Gyms listed'], ['7', 'Official partners & clubs'], ['$35', 'Drop-ins from'], ['10', 'Iron Grit HYROX athletes last season']],
    featuredNav: ['#crossfit-boston-iron-grit', 'Featured: Boston Iron Grit'],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Official partners and training clubs from Back Bay to the Seaport and MetroWest.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-ready', blurb: 'Premier CrossFit boxes and F45/BFT partner formats around the Hub.' },
    ],
    faqs: [
      { q: 'Where can I train for HYROX in Boston?', a: 'Boston Iron Grit (formerly CrossFit Boston Iron & Grit) runs a dedicated HYROX Club in West Roxbury, while official partners like Invictus Boston and Backyard Boston run HYROX-specific classes downtown.' },
      { q: 'How much does Boston Iron Grit cost?', a: '$275/month for Platinum Membership (HYROX Club included), $600 for a 24-session HYROX punch card for new athletes, $50/month to add HYROX training to a current membership, or $35 drop-ins — plus a free goal review to map your first HYROX.' },
      { q: 'Which Boston gyms are official HYROX partners?', a: 'Invictus Boston, Backyard Boston and Beantown Bootcamp are official HYROX training partners or clubs.' },
      { q: 'Does Boston host an official HYROX race?', a: 'Yes — HYROX Boston is a regular North America stop; check hyrox.com for current dates.' },
    ],
  },
  chicago: {
    directoryId: 'chicago-hyrox',
    testPrefix: 'chicago',
    kicker: 'HYROX · Chicago',
    h1: 'The Best HYROX Training Gyms in Chicago',
    jsonLdName: 'The Best HYROX Training Gyms in Chicago',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Chicago</strong>? The city has one of the Midwest's deepest official
        benches — South Loop Strength & Conditioning's dedicated HYROX program, the Chicago Athletic Clubs' accredited network, and
        official clubs from Bucktown to Andersonville.
      </>
    ),
    stats: (count) => [[`${count || 8}`, 'Gyms listed'], ['6', 'Official clubs & accredited'], ['4', 'HYROX program tiers at Cheetah'], ['✓', 'Free intro at South Loop']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified official HYROX Training Clubs from the South Loop to Bucktown, Lakeview and Andersonville.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'Hybrid strength floors and race-prep programs across the city.' },
    ],
    faqs: [
      { q: 'Which Chicago gyms are official HYROX Training Clubs?', a: 'South Loop Strength & Conditioning, Bucktown Athletic Club, Movement Republic, Cheetah Gym, Windy City Strength & Conditioning and the Chicago Athletic Clubs network are all official or accredited HYROX facilities.' },
      { q: 'Where should a HYROX beginner start in Chicago?', a: "South Loop Strength & Conditioning offers a free no-sweat intro consult, and Cheetah Gym's Foundational tier is built specifically for first-time HYROX athletes." },
      { q: 'Does Chicago host an official HYROX race?', a: 'Yes — HYROX Chicago is a regular North America calendar stop; check hyrox.com for current dates and start your 12-week prep block early.' },
    ],
  },
  miami: {
    directoryId: 'miami-hyrox',
    testPrefix: 'miami',
    kicker: 'HYROX · Miami',
    h1: 'The Best HYROX Training Gyms in Miami',
    jsonLdName: 'The Best HYROX Training Gyms in Miami',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Miami</strong>? From HYROX-named studios to hybrid clubs in Wynwood,
        Brickell and the Beach, the 305 has a floor for every kind of racer.
      </>
    ),
    stats: (count) => [[`${count || 12}`, 'Gyms listed'], ['305', 'The Magic City'], ['✓', 'HYROX-named studios'], ['✓', 'Verified Google ratings']],
    areaSections: [
      { key: 'HYROX Programs & Classes', id: 'hyrox-programs', blurb: 'Gyms running HYROX-specific classes, programming or race simulations.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'Strong hybrid floors for station work between race-specific sessions.' },
    ],
    faqs: [
      { q: 'Which Miami gyms run HYROX-specific training?', a: 'Multiple Miami facilities run HYROX-branded classes and programming — check each listing above for schedules, and confirm current official affiliation on hyrox.com.' },
      { q: 'Does Miami host an official HYROX race?', a: 'Yes — Miami is an active HYROX market on the North America calendar; check hyrox.com for current dates.' },
    ],
  },
  dc: {
    directoryId: 'dc-hyrox',
    testPrefix: 'dc',
    kicker: 'HYROX · Washington DC',
    h1: 'The Best HYROX Training Gyms in Washington DC',
    jsonLdName: 'The Best HYROX Training Gyms in Washington DC',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Washington DC</strong>? The DMV runs deep — HYROX programs inside the
        District plus race-ready strength floors in Arlington, Bethesda and Alexandria.
      </>
    ),
    stats: (count) => [[`${count || 10}`, 'Gyms listed'], ['DMV', 'DC · MD · VA coverage'], ['✓', 'HYROX programs'], ['✓', 'Verified Google ratings']],
    areaSections: [
      { key: 'HYROX Programs & Classes', id: 'hyrox-programs', blurb: 'Gyms running HYROX-specific classes, programming or race simulations.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'Strong hybrid floors for station work between race-specific sessions.' },
    ],
    faqs: [
      { q: 'Which DC gyms run HYROX-specific training?', a: 'Several DMV facilities run HYROX classes and programming — check each listing above for schedules and confirm current affiliation on hyrox.com.' },
      { q: 'Does Washington DC host an official HYROX race?', a: 'Yes — DC is on the HYROX North America calendar; check hyrox.com for current dates.' },
    ],
  },
  phoenix: {
    directoryId: 'phoenix-hyrox',
    testPrefix: 'phoenix',
    kicker: 'HYROX · Phoenix',
    h1: 'The Best HYROX Training Gyms in Phoenix',
    jsonLdName: 'The Best HYROX Training Gyms in Phoenix',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Phoenix</strong>? The Valley brings serious hybrid firepower — HYROX
        programs from Scottsdale to Tempe with sled-friendly turf and desert-hardened engines.
      </>
    ),
    stats: (count) => [[`${count || 9}`, 'Gyms listed'], ['PHX', 'Phoenix + Scottsdale'], ['✓', 'HYROX programs'], ['✓', 'Verified Google ratings']],
    areaSections: [
      { key: 'HYROX Programs & Classes', id: 'hyrox-programs', blurb: 'Gyms running HYROX-specific classes, programming or race simulations.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'Strong hybrid floors for station work between race-specific sessions.' },
    ],
    faqs: [
      { q: 'Which Phoenix gyms run HYROX-specific training?', a: 'Multiple Valley facilities run HYROX classes and programming — check each listing above for schedules and confirm current affiliation on hyrox.com.' },
      { q: 'Does Phoenix host an official HYROX race?', a: 'Yes — Phoenix appears on the HYROX North America calendar; check hyrox.com for current dates.' },
    ],
  },
  nyc: {
    directoryId: 'nyc-hyrox',
    testPrefix: 'nyc',
    kicker: 'HYROX · New York City',
    h1: 'The Best HYROX Training Gyms in New York City',
    jsonLdName: 'The Best HYROX Training Gyms in New York City',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in New York City</strong>? The list is compact but elite — dedicated
        race-prep programs across Manhattan and Brooklyn with official-standard stations.
      </>
    ),
    stats: (count) => [[`${count || 4}`, 'Gyms listed'], ['NYC', 'Manhattan + Brooklyn'], ['✓', 'HYROX programs'], ['✓', 'Verified Google ratings']],
    areaSections: [
      { key: 'HYROX Programs & Classes', id: 'hyrox-programs', blurb: 'Gyms running HYROX-specific classes, programming or race simulations.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'Strong hybrid floors for station work between race-specific sessions.' },
    ],
    faqs: [
      { q: 'Which NYC gyms run HYROX-specific training?', a: 'The gyms above run HYROX classes and programming across Manhattan and Brooklyn — confirm current schedules directly and check hyrox.com for official affiliations.' },
      { q: 'Does New York host an official HYROX race?', a: 'Yes — NYC is a marquee HYROX North America stop; check hyrox.com for current dates.' },
    ],
  },
  la: {
    directoryId: 'la-hyrox',
    testPrefix: 'la',
    kicker: 'HYROX · Los Angeles',
    h1: 'The Best HYROX Training Gyms in Los Angeles',
    jsonLdName: 'The Best HYROX Training Gyms in Los Angeles',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Los Angeles</strong>? From DTLA warehouse floors to Beverly Hills
        studios, LA's official Training Clubs cover every corner of the city.
      </>
    ),
    stats: (count) => [[`${count || 7}`, 'Gyms listed'], ['5', 'Official Training Clubs'], ['LA', 'DTLA to Beverly Hills'], ['✓', 'Race-standard stations']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified official HYROX Training Clubs across the city.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'Run-strength studios and hybrid floors that map to race demands.' },
    ],
    faqs: [
      { q: 'Which LA gyms are official HYROX Training Clubs?', a: 'Backyard Fitness (DTLA), K2 CrossFit (Koreatown), Zero2Hero Fitness, CrossFit Muse and STUDEO Beverly Hills are official HYROX Training Clubs in Los Angeles.' },
      { q: 'Does Los Angeles host an official HYROX race?', a: 'Yes — LA is a regular HYROX North America stop, and nearby Anaheim hosts races at the Convention Center; check hyrox.com for dates.' },
    ],
  },
  atlanta: {
    directoryId: 'atlanta-hyrox',
    testPrefix: 'atlanta',
    kicker: 'HYROX · Atlanta',
    h1: 'The Best HYROX Training Gyms in Atlanta',
    jsonLdName: 'The Best HYROX Training Gyms in Atlanta',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Atlanta</strong>? The Westside and BeltLine corridors are stacked with
        official Training Clubs and hybrid performance gyms.
      </>
    ),
    stats: (count) => [[`${count || 7}`, 'Gyms listed'], ['4', 'Official Training Clubs'], ['ATL', 'Westside to Brookhaven'], ['✓', 'Race-standard stations']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified official HYROX Training Clubs across the metro.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'HYROX-format classes and hybrid strength floors.' },
    ],
    faqs: [
      { q: 'Which Atlanta gyms are official HYROX Training Clubs?', a: 'The Refinery, The Union Fit Hub, Carbon Performance Atlanta and Noble Clay Fitness are official HYROX Training Clubs in Atlanta.' },
      { q: 'Does Atlanta host an official HYROX race?', a: 'Yes — Atlanta is on the HYROX North America calendar; check hyrox.com for current dates.' },
    ],
  },
  charlotte: {
    directoryId: 'charlotte-hyrox',
    testPrefix: 'charlotte',
    kicker: 'HYROX · Charlotte',
    h1: 'The Best HYROX Training Gyms in Charlotte',
    jsonLdName: 'The Best HYROX Training Gyms in Charlotte',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Charlotte</strong>? The NoDa corridor anchors the Queen City's scene —
        intro HYROX classes, quarterly race simulations and hybrid performance clubs.
      </>
    ),
    stats: (count) => [[`${count || 6}`, 'Gyms listed'], ['NoDa', 'The HYROX corridor'], ['4×', 'Race sims per year'], ['✓', 'Intro classes for beginners']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'HYROX classes, simulations and race-prep programs.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'Interval studios and hybrid floors that translate to race day.' },
    ],
    faqs: [
      { q: 'Where can I do a HYROX simulation in Charlotte?', a: 'Charlotte Strength (NoDa Fit) at 624 Anderson St runs quarterly full HYROX race simulations plus intro classes for first-timers.' },
      { q: 'Does Charlotte host an official HYROX race?', a: 'Check hyrox.com for the current North America calendar — and use the quarterly sims at Charlotte Strength to test pacing either way.' },
    ],
  },
  minneapolis: {
    directoryId: 'minneapolis-hyrox',
    testPrefix: 'minneapolis',
    kicker: 'HYROX · Minneapolis',
    h1: 'The Best HYROX Training Gyms in Minneapolis',
    jsonLdName: 'The Best HYROX Training Gyms in Minneapolis',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in Minneapolis</strong>? The Twin Cities hub runs through Tonka Fitness —
        the official affiliate — plus scheduled HYROX classes across St. Louis Park, Highland Park and the west metro.
      </>
    ),
    stats: (count) => [[`${count || 5}`, 'Gyms listed'], ['1', 'Official HYROX affiliate'], ['MSP', 'Twin Cities coverage'], ['Sat', 'AM HYROX at SISU']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'The official affiliate and gyms with scheduled HYROX classes.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'HYROX-ready strength floors across the metro.' },
    ],
    faqs: [
      { q: 'Which Minneapolis gym is the official HYROX affiliate?', a: 'Tonka Fitness in Hopkins is the Twin Cities\' official HYROX affiliate, running dedicated classes and full race simulations.' },
      { q: 'Does Minneapolis host an official HYROX race?', a: 'Check hyrox.com for the current North America calendar — Tonka Fitness race sims are the best local dress rehearsal either way.' },
    ],
  },
  sf: {
    directoryId: 'sf-hyrox',
    testPrefix: 'sf',
    kicker: 'HYROX · San Francisco',
    h1: 'The Best HYROX Training Gyms in San Francisco',
    jsonLdName: 'The Best HYROX Training Gyms in San Francisco',
    intro: (
      <>
        Training for <strong className="text-zinc-200">HYROX in San Francisco</strong>? Official Training Clubs cluster around Hayes
        Valley and SoMa, with race-prep boxes across the East, South and North Bay.
      </>
    ),
    stats: (count) => [[`${count || 7}`, 'Gyms listed'], ['2', 'Official Training Clubs'], ['Bay', 'SF to San Jose coverage'], ['✓', 'Mock races in the North Bay']],
    areaSections: [
      { key: 'Official HYROX Training Clubs', id: 'official-training-clubs', blurb: 'Verified official HYROX Training Clubs in the city.' },
      { key: 'More HYROX-Ready Gyms', id: 'more-hyrox-gyms', blurb: 'HYROX programs and hybrid boxes across the greater Bay Area.' },
    ],
    faqs: [
      { q: 'Which San Francisco gyms are official HYROX Training Clubs?', a: 'Custom Fit (Hayes Valley) and Flagship Athletic Performance (Church St) are official HYROX Training Clubs in San Francisco proper.' },
      { q: 'Where can I do HYROX race prep in the greater Bay Area?', a: 'CrossFit Sweat Shop (Walnut Creek) runs HYROX sessions Thursdays and Sundays, CrossFit Silicon Valley (San Jose) has a structured program, and Pinnacle Fitness (Fairfield) runs a 7-day seasonal program with mock races.' },
    ],
  },
};

const CITY_LINKS = {
  dfw: { label: 'Dallas–Fort Worth', slug: 'the-best-hyrox-training-gyms-in-dallas-fort-worth-your-complete-directory', blurb: '30 gyms · official HYROX Training Center · drop-ins from $15' },
  houston: { label: 'Houston', slug: 'the-best-hyrox-training-gyms-in-houston-your-complete-directory', blurb: '20 gyms · HTC-X certified facility · first classes from $25' },
  austin: { label: 'Austin', slug: 'the-best-hyrox-training-gyms-in-austin-your-complete-directory', blurb: '12 gyms · 8 official Training Clubs · day passes from $19' },
  denver: { label: 'Denver', slug: 'the-best-hyrox-training-gyms-in-denver-your-complete-directory', blurb: '13 gyms · Arsenal Health featured · 24/7 fob access' },
  tampa: { label: 'Tampa', slug: 'the-best-hyrox-training-gyms-in-tampa-your-complete-directory', blurb: '15 gyms · F45 Largo East featured · full HYROX sim Sep 12' },
  slc: { label: 'Salt Lake City', slug: 'the-best-hyrox-training-gyms-in-salt-lake-city-your-complete-directory', blurb: '15 gyms · F45 Sugarhouse featured · 5-for-$35 intro' },
  vegas: { label: 'Las Vegas', slug: 'the-best-hyrox-training-gyms-in-las-vegas-your-complete-directory', blurb: '13 gyms · 9 official Training Clubs' },
  portland: { label: 'Portland', slug: 'the-best-hyrox-training-gyms-in-portland-your-complete-directory', blurb: '6 gyms · official F45 Pearl District affiliate · DPT-led sims' },
  anaheim: { label: 'Anaheim', slug: 'the-best-hyrox-training-gyms-in-anaheim-your-complete-directory', blurb: '5 gyms · official HYROX Training Clubs · Irvine to Costa Mesa' },
  sandiego: { label: 'San Diego', slug: 'the-best-hyrox-training-gyms-in-san-diego-your-complete-directory', blurb: '11 gyms · MROC Training featured · 3 classes for $30' },
  nashville: { label: 'Nashville', slug: 'the-best-hyrox-training-gyms-in-nashville-your-complete-directory', blurb: '15 gyms · Blueprint Fitness featured · free pass' },
  boston: { label: 'Boston', slug: 'the-best-hyrox-training-gyms-in-boston-your-complete-directory', blurb: '13 gyms · Boston Iron Grit featured · drop-ins from $35' },
  chicago: { label: 'Chicago', slug: 'the-best-hyrox-training-gyms-in-chicago-your-complete-directory', blurb: '8 gyms · 6 official clubs · free intro at South Loop S&C' },
  miami: { label: 'Miami', slug: 'the-best-hyrox-training-gyms-in-miami-your-complete-directory', blurb: '12 gyms · HYROX-named studios · Wynwood to the Beach' },
  dc: { label: 'Washington DC', slug: 'the-best-hyrox-training-gyms-in-washington-dc-your-complete-directory', blurb: '10 gyms · DMV coverage · DC, Arlington & Bethesda' },
  phoenix: { label: 'Phoenix', slug: 'the-best-hyrox-training-gyms-in-phoenix-your-complete-directory', blurb: '9 gyms · Scottsdale to Tempe · HYROX programs' },
  nyc: { label: 'New York City', slug: 'the-best-hyrox-training-gyms-in-new-york-city-your-complete-directory', blurb: '4 gyms · Manhattan + Brooklyn · elite race prep' },
  la: { label: 'Los Angeles', slug: 'the-best-hyrox-training-gyms-in-los-angeles-your-complete-directory', blurb: '7 gyms · 5 official Training Clubs · DTLA to Beverly Hills' },
  atlanta: { label: 'Atlanta', slug: 'the-best-hyrox-training-gyms-in-atlanta-your-complete-directory', blurb: '7 gyms · 4 official Training Clubs · Westside & BeltLine' },
  charlotte: { label: 'Charlotte', slug: 'the-best-hyrox-training-gyms-in-charlotte-your-complete-directory', blurb: '6 gyms · NoDa corridor · quarterly race sims' },
  minneapolis: { label: 'Minneapolis', slug: 'the-best-hyrox-training-gyms-in-minneapolis-your-complete-directory', blurb: '5 gyms · Tonka Fitness official affiliate · Twin Cities' },
  sf: { label: 'San Francisco', slug: 'the-best-hyrox-training-gyms-in-san-francisco-your-complete-directory', blurb: '7 gyms · 2 official Training Clubs · Bay-wide coverage' },
};

const LOCATOR_LINKS = {
  dfw: { slug: 'dallas-fort-worth', name: 'Dallas–Fort Worth' },
  austin: { slug: 'austin', name: 'Austin' },
  denver: { slug: 'denver', name: 'Denver' },
  tampa: { slug: 'tampa', name: 'Tampa' },
  slc: { slug: 'salt-lake-city', name: 'Salt Lake City' },
  vegas: { slug: 'las-vegas', name: 'Las Vegas' },
  anaheim: { slug: 'orange-county', name: 'Orange County' },
  sandiego: { slug: 'san-diego', name: 'San Diego' },
  nashville: { slug: 'nashville', name: 'Nashville' },
  chicago: { slug: 'chicago', name: 'Chicago' },
  miami: { slug: 'miami', name: 'Miami' },
  dc: { slug: 'washington-dc', name: 'Washington DC' },
  phoenix: { slug: 'phoenix', name: 'Phoenix' },
  nyc: { slug: 'new-york', name: 'New York' },
  la: { slug: 'los-angeles', name: 'Los Angeles' },
  atlanta: { slug: 'atlanta', name: 'Atlanta' },
  minneapolis: { slug: 'minneapolis', name: 'Minneapolis' },
  sf: { slug: 'bay-area', name: 'the Bay Area' },
};

const useDirectoryJsonLd = (gyms, cfg) => {
  useEffect(() => {
    if (!gyms?.length) return;
    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: cfg.jsonLdName,
      numberOfItems: gyms.length,
      itemListElement: gyms.map((g, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'ExerciseGym',
          name: g.name,
          description: g.description,
          ...(g.address ? { address: g.address } : {}),
          ...(g.phone ? { telephone: g.phone } : {}),
          ...(g.website ? { url: g.website } : {}),
          ...(g.rating ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: g.rating, reviewCount: String(g.review_count).replace(/\D/g, '') || '1' } } : {}),
        },
      })),
    };
    const faqPage = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: cfg.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const inject = (id, data) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
      return el;
    };
    const a = inject(`${cfg.testPrefix}-itemlist-jsonld`, itemList);
    const b = inject(`${cfg.testPrefix}-faq-jsonld`, faqPage);
    return () => { a.remove(); b.remove(); };
  }, [gyms, cfg]);
};

const FeaturedGym = ({ gym, onAddVideo, testPrefix, locatorMetro }) => (
  <section id={gym.gym_id} className="scroll-mt-28 mb-16" data-testid={`${testPrefix}-featured-gym`}>
    <p className="text-[#E63946] text-xs uppercase tracking-[0.25em] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
      Featured Gym{gym.neighborhood ? ` · ${gym.neighborhood}` : ''}
    </p>
    {gym.logo && (
      <span className={gym.logo_bg === 'light' ? 'inline-block bg-white rounded px-3 py-2 mb-3' : 'inline-block mb-3'}>
        <img src={gym.logo} alt={`${gym.name} logo`} className="h-12 w-auto" loading="lazy" data-testid={`${testPrefix}-featured-logo`} />
      </span>
    )}
    <h2 className="text-white text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
      {gym.tagline ? `${gym.name} — ${gym.tagline.split('—')[0].trim()}` : gym.name}
    </h2>

    <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-8">
      <div>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {gym.rating && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#C7FA3E]/10 text-[#C7FA3E] border border-[#C7FA3E]/30">
              <Star className="w-3 h-3 fill-current" /> {gym.rating} · {gym.review_count} reviews
            </span>
          )}
          {gym.badges?.map((b) => (
            <span key={b} className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-300 border border-zinc-700">{b}</span>
          ))}
          {gym.trial_offer && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#C7FA3E]/10 text-[#C7FA3E] border border-[#C7FA3E]/30" data-testid={`${testPrefix}-featured-trial`}>
              <Ticket className="w-3 h-3" /> {gym.trial_offer}
            </span>
          )}
        </div>
        <p className="text-zinc-300 text-sm leading-relaxed mb-5">{gym.description}</p>
        {gym.coaches?.map((c) => (
          <div key={c.name} className="mb-3 pl-3 border-l-2 border-[#E63946]/60">
            <p className="text-white text-sm font-semibold">
              {c.name}{' '}
              {c.instagram && (
                <a href={c.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[#C7FA3E] text-xs hover:underline">
                  <Instagram className="w-3 h-3" /> {c.instagram.split('/').filter(Boolean).pop()}
                </a>
              )}
            </p>
            <p className="text-zinc-500 text-xs leading-relaxed">{c.bio}</p>
          </div>
        ))}
        {gym.schedule_note && (
          <p className="text-zinc-500 text-xs mb-4 flex items-start gap-1.5" data-testid={`${testPrefix}-featured-schedule-note`}>
            <Clock className="w-3 h-3 flex-shrink-0 mt-0.5" /> {gym.schedule_note}
          </p>
        )}
        {gym.schedules?.length > 0 && (
          <div className="mb-4" data-testid={`${testPrefix}-featured-schedule`}>
            <p className="text-white text-xs uppercase tracking-[0.2em] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>HYROX Class Times</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {gym.schedules.map((s) => (
                <div key={s.location} className="pl-3 border-l-2 border-[#C7FA3E]/50">
                  <p className="text-[#C7FA3E] text-xs font-semibold uppercase mb-1" style={{ letterSpacing: '0.12em', wordSpacing: '0.25em' }}>{s.location}</p>
                  {s.times.map((t) => <p key={t} className="text-zinc-400 text-xs leading-relaxed">{t}</p>)}
                </div>
              ))}
            </div>
          </div>
        )}
        {gym.coach_handles?.length > 0 && (
          <div className="mb-4" data-testid={`${testPrefix}-featured-coaches`}>
            <p className="text-white text-xs uppercase tracking-[0.2em] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Coaching Team</p>
            <div className="flex flex-wrap gap-1.5">
              {gym.coach_handles.map((h) => (
                <a key={h} href={`https://www.instagram.com/${h}/`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-zinc-800/80 text-zinc-300 border border-zinc-700 hover:border-[#C7FA3E]/50 hover:text-[#C7FA3E] transition-colors">
                  <Instagram className="w-3 h-3" /> @{h}
                </a>
              ))}
            </div>
          </div>
        )}
        {gym.socials?.length > 0 && (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500 mb-2" data-testid={`${testPrefix}-featured-socials`}>
            <span className="uppercase tracking-wider">Follow:</span>
            {gym.socials.map((s) => (
              <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#C7FA3E] hover:underline">{s.label}</a>
            ))}
          </p>
        )}
        {gym.discount_note && (
          <p className="inline-flex items-center gap-1.5 mt-2 px-3 py-2 text-xs text-[#C7FA3E] bg-[#C7FA3E]/[0.06] border border-[#C7FA3E]/25" data-testid={`${testPrefix}-featured-discount`}>
            <Tag className="w-3.5 h-3.5" /> {gym.discount_note}
          </p>
        )}

        <NearbyRecovery gymId={gym.gym_id} metro={locatorMetro} />
      </div>

      <div className="lg:h-full">
        {gym.video ? (
          <div className={`flex flex-col gap-2 lg:h-full ${!gym.photos?.length ? 'lg:justify-center' : ''}`}>
            <VideoEmbed video={gym.video} name={gym.name} />
            {gym.video.title && <p className="text-zinc-500 text-xs">{gym.video.caption || `Member POV — ${gym.video.title}`}</p>}
            {gym.photos?.length > 0 && (
              <div className={`grid gap-2 lg:flex-1 lg:auto-rows-fr ${gym.photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid={`${testPrefix}-featured-photos`}>
                {gym.photos.slice(0, 4).map((p, i) => (
                  <img key={p} src={p} alt={`${gym.name} — facility ${i + 1}`} className="w-full h-36 lg:h-full lg:min-h-28 object-cover" loading="lazy" />
                ))}
              </div>
            )}
          </div>
        ) : gym.photos?.length > 0 ? (
          <div className="flex flex-col gap-2 lg:h-full" data-testid={`${testPrefix}-featured-photos`}>
            <img src={gym.photos[0]} alt={`${gym.name} — training floor`} className="w-full h-56 object-cover" loading="lazy" />
            {gym.photos.length > 1 && (
              <div className={`grid gap-2 lg:flex-1 lg:auto-rows-fr ${gym.photos.length === 2 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {gym.photos.slice(1, 5).map((p, i) => (
                  <img key={p} src={p} alt={`${gym.name} — facility ${i + 2}`} className="w-full h-36 lg:h-full lg:min-h-28 object-cover" loading="lazy" />
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>

    {gym.pricing?.length > 0 && (
      <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid={`${testPrefix}-featured-pricing`}>
        {gym.pricing.map((p) => (
          <div
            key={p.label}
            className={`relative p-5 border ${p.popular ? 'border-[#E63946] bg-[#E63946]/[0.05]' : 'border-zinc-800 bg-zinc-900/50'}`}
          >
            {p.popular && (
              <span className="absolute -top-2.5 left-4 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-[#E63946] text-white">Most Popular</span>
            )}
            <p className="text-zinc-400 text-xs uppercase tracking-widest mb-1">{p.label}</p>
            <p className="text-white text-3xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              {p.price}<span className="text-zinc-500 text-base">{p.period}</span>
            </p>
            <p className="text-zinc-500 text-xs mb-4">{p.note}</p>
            <a
              href={p.url || gym.buy_class_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className={`inline-flex w-full items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                p.popular ? 'bg-[#E63946] hover:bg-[#c62d39] text-white' : 'border border-zinc-600 hover:border-white text-zinc-200'
              }`}
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid={`${testPrefix}-featured-buy-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <Zap className="w-3.5 h-3.5" /> {p.cta || `Buy ${p.label}`}
            </a>
          </div>
        ))}
      </div>
    )}

    {gym.pricing_note && <p className="text-zinc-400 text-xs -mt-3 mb-6 max-w-3xl" data-testid={`${testPrefix}-featured-pricing-note`}>{gym.pricing_note}</p>}

    <UpcomingClasses gym={gym} />

    {gym.events?.filter((e) => !e.expires || new Date(e.expires) > new Date()).map((e) => (
      <div key={e.name} className="border border-[#C7FA3E]/30 bg-[#C7FA3E]/5 p-4 mb-3 max-w-2xl" data-testid={`${testPrefix}-featured-event-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
        <p className="text-[#C7FA3E] text-[10px] font-bold uppercase tracking-widest mb-0.5">Upcoming Event</p>
        <p className="text-white text-sm font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>{e.name}</p>
        <p className="text-zinc-400 text-xs">{e.date}</p>
        {e.description && <p className="text-zinc-500 text-xs mt-1">{e.description}</p>}
        {e.url && (
          <a href={e.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-[#C7FA3E] text-black text-[11px] font-bold uppercase tracking-widest hover:opacity-90 transition-opacity"
            data-testid={`${testPrefix}-featured-event-cta-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            {e.cta_label || 'Register'} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    ))}

    {gym.extra_ctas?.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-4 mb-6" data-testid={`${testPrefix}-featured-extra-ctas`}>
        {gym.extra_ctas.map((c) => (
          <a key={c.label} href={c.url} target="_blank" rel="noopener noreferrer sponsored"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-xs uppercase tracking-widest transition-colors"
            data-testid={`${testPrefix}-featured-extra-cta-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            {c.label} <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
    )}

    {!gym.pricing?.length && gym.buy_class_url && (
      <div className="mb-6">
        <a
          href={gym.buy_class_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#E63946] hover:bg-[#c62d39] text-white text-sm font-bold uppercase tracking-widest transition-colors"
          style={{ fontFamily: 'Oswald, sans-serif' }}
          data-testid={`${testPrefix}-featured-cta`}
        >
          <Zap className="w-4 h-4" /> {gym.cta_label || 'Buy a Class'}
        </a>
      </div>
    )}

    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-zinc-500">
      {gym.address && (
        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(gym.address)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-zinc-300">
          <MapPin className="w-3 h-3 text-[#E63946]" /> {gym.address}
        </a>
      )}
      {gym.address_2 && (
        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(gym.address_2.replace(/^[A-Za-z]+: /, ''))}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-zinc-300">
          <MapPin className="w-3 h-3 text-[#E63946]" /> {gym.address_2}
        </a>
      )}
      {gym.phone && (
        <a href={`tel:${gym.phone.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-1 hover:text-zinc-300">
          <Phone className="w-3 h-3" /> {gym.phone}
        </a>
      )}
      {gym.hours && (
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {gym.hours}</span>
      )}
      {gym.website && (
        <a href={gym.website} target="_blank" rel="noopener noreferrer sponsored" className="inline-flex items-center gap-1 text-[#C7FA3E] hover:underline" data-testid={`${testPrefix}-featured-website`}>
          <Globe className="w-3 h-3" /> Visit Website
        </a>
      )}
      <button
        onClick={scrollToRaceAlerts}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-wider border border-zinc-800 text-zinc-500 hover:text-[#C7FA3E] hover:border-[#C7FA3E]/40 transition-colors"
        data-testid={`${testPrefix}-featured-race-alert-chip`}
      >
        <Bell className="w-3 h-3" /> Get race alerts
      </button>
    </div>
  </section>
);

const MoreCities = ({ currentCity }) => (
  <section className="mb-12" data-testid="more-hyrox-cities">
    <h2 className="text-white text-2xl sm:text-3xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
      HYROX Gym Directories in More Cities
    </h2>
    <p className="text-zinc-500 text-sm mb-6">Racing somewhere else? We vet gyms city by city.</p>
    <div className="grid sm:grid-cols-2 gap-4">
      {Object.entries(CITY_LINKS)
        .filter(([key]) => key !== currentCity)
        .map(([key, c]) => (
          <Link
            key={key}
            to={`/blogs/article/${c.slug}`}
            className="group flex items-center justify-between gap-4 p-5 bg-zinc-900/50 border border-zinc-800 hover:border-[#C7FA3E]/50 transition-colors"
            data-testid={`city-link-${key}`}
          >
            <div>
              <p className="text-white text-lg" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
                Best HYROX Gyms in {c.label}
              </p>
              <p className="text-zinc-500 text-xs">{c.blurb}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-[#C7FA3E] group-hover:translate-x-1 transition-all flex-shrink-0" />
          </Link>
        ))}
    </div>
    <Link to="/hyrox/gyms" className="inline-flex items-center gap-1.5 mt-4 text-[#C7FA3E] text-xs uppercase tracking-widest hover:gap-2.5 transition-all" data-testid="view-all-cities-link" style={{ fontFamily: 'Oswald, sans-serif' }}>
      View all city directories <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </section>
);

export default function CityHyroxDirectory({ cityId = 'dfw' }) {
  const cfg = CITY_CONFIGS[cityId];
  const [gyms, setGyms] = useState([]);
  const [videoGym, setVideoGym] = useState(null);
  const [claimGym, setClaimGym] = useState(null);
  const [trialOnly, setTrialOnly] = useState(false);

  useEffect(() => {
    setGyms([]);
    setTrialOnly(false);
    directoryApi.getDirectory(cfg.directoryId).then(({ data }) => setGyms(data.gyms || [])).catch(() => {});
  }, [cfg.directoryId]);

  useEffect(() => {
    if (!gyms.length) return undefined;
    const hash = window.location.hash?.slice(1);
    if (!hash) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(t);
  }, [gyms]);

  useDirectoryJsonLd(gyms, cfg);

  const trialCount = useMemo(() => gyms.filter((g) => g.trial_offer).length, [gyms]);
  const visibleGyms = useMemo(() => (trialOnly ? gyms.filter((g) => g.trial_offer) : gyms), [gyms, trialOnly]);
  const featuredGyms = useMemo(() => visibleGyms.filter((g) => g.featured), [visibleGyms]);
  const byArea = useMemo(() => {
    const m = {};
    visibleGyms.filter((g) => !g.featured).forEach((g) => {
      (m[g.area] = m[g.area] || []).push(g);
    });
    return m;
  }, [visibleGyms]);

  const quickNav = [
    ...(cfg.featuredNav ? (Array.isArray(cfg.featuredNav[0]) ? cfg.featuredNav : [cfg.featuredNav]) : []),
    ...cfg.areaSections.map(({ key, id }) => [`#${id}`, key]),
    ['#directory-faq', 'FAQ'],
  ];

  const gymById = useMemo(() => Object.fromEntries(gyms.map((g) => [g.gym_id, g])), [gyms]);

  const handleOutboundClick = (e) => {
    const a = e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('#') || href.startsWith('/')) return;
    let node = a;
    let gym = null;
    while (node && node !== e.currentTarget) {
      if (node.id && gymById[node.id]) { gym = gymById[node.id]; break; }
      node = node.parentElement;
    }
    if (!gym) return;
    let kind = 'other';
    if (href.startsWith('tel:')) kind = 'phone';
    else if (href.startsWith('mailto:')) kind = 'email';
    else if (/instagram\.com|facebook\.com|tiktok\.com/i.test(href)) kind = 'social';
    else if (/google\.com\/maps/i.test(href)) kind = 'maps';
    else if (gym.buy_class_url === href) kind = 'buy_class';
    else if ((gym.pricing || []).some((p) => p.url === href)) kind = 'pricing';
    else if (gym.website === href) kind = 'website';
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/directory/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        gym_id: gym.gym_id,
        directory: cfg.directoryId,
        kind,
        href: href.slice(0, 300),
        label: (a.textContent || '').trim().slice(0, 80),
      }),
      keepalive: true,
    }).catch(() => {});
  };

  return (
    <article className="pb-8" data-testid={`${cfg.testPrefix}-hyrox-directory`} onClickCapture={handleOutboundClick}>
      {/* Hero */}
      <header className="max-w-5xl mx-auto px-6 pt-10 pb-8">
        <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          {cfg.kicker} · Updated {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl mb-5" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
          {cfg.h1}
        </h1>
        <p className="text-zinc-400 text-base leading-relaxed max-w-3xl mb-6">{cfg.intro}</p>

        {/* Stats strip */}
        <div className="flex flex-wrap gap-6 mb-6" data-testid={`${cfg.testPrefix}-stats-strip`}>
          {cfg.stats(gyms.length).map(([v, l]) => (
            <div key={l}>
              <p className="text-[#E63946] text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{v}</p>
              <p className="text-zinc-500 text-[11px] uppercase tracking-wider">{l}</p>
            </div>
          ))}
        </div>

        {/* Quick nav */}
        <nav className="flex flex-wrap gap-2" data-testid={`${cfg.testPrefix}-quick-nav`}>
          {quickNav.map(([href, label]) => (
            <a key={href} href={href} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors">
              {label}
            </a>
          ))}
        </nav>

        {trialCount > 0 && (
          <div className="flex flex-wrap items-center gap-3 mt-5" data-testid={`${cfg.testPrefix}-trial-filter-row`}>
            <button
              onClick={() => setTrialOnly((v) => !v)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 text-xs uppercase tracking-wider border transition-colors ${
                trialOnly
                  ? 'bg-[#C7FA3E] text-black border-[#C7FA3E] font-bold'
                  : 'text-[#C7FA3E] border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.06] hover:bg-[#C7FA3E]/[0.12]'
              }`}
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid={`${cfg.testPrefix}-trial-filter-chip`}
            >
              <Ticket className="w-3.5 h-3.5" /> Free Pass / Trial offers ({trialCount})
            </button>
            {trialOnly && (
              <button
                onClick={() => setTrialOnly(false)}
                className="text-zinc-500 text-xs hover:text-white transition-colors"
                data-testid={`${cfg.testPrefix}-trial-filter-clear`}
              >
                Show all gyms
              </button>
            )}
          </div>
        )}
      </header>

      <div className="max-w-5xl mx-auto px-6">
        {cfg.alertBanner && <RaceAlertCapture cityId={cityId} cityLabel={CITY_LINKS[cityId].label} variant="banner" eventNote={cfg.alertBanner} />}

        {featuredGyms.map((g, i) => (
          <FeaturedGym key={g.gym_id} gym={g} onAddVideo={setVideoGym} testPrefix={i === 0 ? cfg.testPrefix : `${cfg.testPrefix}-f${i + 1}`} locatorMetro={LOCATOR_LINKS[cityId]} />
        ))}

        {cfg.areaSections.map(({ key, id, blurb }) => (
          (byArea[key]?.length > 0) && (
            <section key={id} id={id} className="mb-14 scroll-mt-28" data-testid={`${cfg.testPrefix}-section-${id}`}>
              <h2 className="text-white text-2xl sm:text-3xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>{key}</h2>
              <p className="text-zinc-500 text-sm mb-6">{blurb}</p>
              <div className="grid grid-cols-1 gap-6">
                {byArea[key].map((g) => (
                  <DfwGymCard key={g.gym_id} gym={g} onAddVideo={setVideoGym} onClaim={setClaimGym} locatorMetro={LOCATOR_LINKS[cityId]} />
                ))}
              </div>
            </section>
          )
        ))}

        {/* Race alert email capture */}
        <RaceAlertCapture cityId={cityId} cityLabel={CITY_LINKS[cityId].label} />

        {/* Cross-promo (internal SEO link) */}
        {cfg.crossPromo && (
          <aside className="mb-12 border border-zinc-800 bg-zinc-900/50 p-6 sm:p-8" data-testid={`${cfg.testPrefix}-cross-promo`}>
            <p className="text-[#C7FA3E] text-xs uppercase tracking-[3px] mb-2">{cfg.crossPromo.eyebrow}</p>
            <h3 className="text-white text-2xl sm:text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>{cfg.crossPromo.title}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-5 max-w-3xl">{cfg.crossPromo.text}</p>
            <Link to={cfg.crossPromo.href} className="inline-flex items-center gap-2 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-wider px-5 py-3 hover:bg-[#d4ff5c] transition-colors" data-testid={`${cfg.testPrefix}-cross-promo-cta`}>
              {cfg.crossPromo.cta} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </aside>
        )}

        {/* Recovery locator cross-link (internal SEO link) */}
        {LOCATOR_LINKS[cityId] && (
          <aside className="mb-12 border border-zinc-800 bg-zinc-900/50 p-6 sm:p-8" data-testid={`${cfg.testPrefix}-locator-cross-link`}>
            <p className="text-[#C7FA3E] text-xs uppercase tracking-[3px] mb-2">Also on NinjAthlete</p>
            <h3 className="text-white text-2xl sm:text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
              Recovery &amp; Peptide Therapy in {LOCATOR_LINKS[cityId].name}
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-5 max-w-3xl">
              HYROX training breaks you down — smart recovery builds you back. Our vetted locator covers cold plunge, sauna, HBOT, IV therapy
              and peptide clinics across {LOCATOR_LINKS[cityId].name}, with contact details and direct booking links for every facility.
            </p>
            <Link to={`/locator/${LOCATOR_LINKS[cityId].slug}`} className="inline-flex items-center gap-2 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-wider px-5 py-3 hover:bg-[#d4ff5c] transition-colors" data-testid={`${cfg.testPrefix}-locator-cta`}>
              Browse recovery clinics in {LOCATOR_LINKS[cityId].name} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </aside>
        )}

        {/* FAQ */}
        <section id="directory-faq" className="mb-12 scroll-mt-28" data-testid={`${cfg.testPrefix}-faq-section`}>
          <h2 className="text-white text-2xl sm:text-3xl mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-2">
            {cfg.faqs.map((f, i) => (
              <details key={i} className="group bg-zinc-900/50 border border-zinc-800">
                <summary className="flex items-center justify-between gap-4 px-4 py-3.5 cursor-pointer list-none text-white text-sm font-medium">
                  {f.q}
                  <ChevronDown className="w-4 h-4 text-zinc-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                </summary>
                <p className="px-4 pb-4 text-zinc-400 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Cross-city links */}
        <MoreCities currentCity={cityId} />

        {/* Internal links */}
        <div className="p-4 bg-zinc-900/60 border border-zinc-800 text-sm text-zinc-400" data-testid={`${cfg.testPrefix}-protip`}>
          <strong className="text-white">Training for HYROX?</strong> Start with our{' '}
          <a href="/hyrox" className="text-[#C7FA3E] hover:underline">complete HYROX command center</a> and the{' '}
          <a href="/blogs/article/the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready" className="text-[#C7FA3E] hover:underline">ultimate HYROX training plan</a>.
          Recovering between sessions? See our{' '}
          <a href="/blogs/article/the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing" className="text-[#C7FA3E] hover:underline">peptide recovery stack guide</a>.
        </div>
      </div>

      {videoGym && <VideoSubmitModal gym={videoGym} onClose={() => setVideoGym(null)} />}
      {claimGym && <ClaimListingModal gym={claimGym} onClose={() => setClaimGym(null)} />}
    </article>
  );
}
